/**
 * SVG glyphs for the abstract floorplan editor canvas.
 * Pure-presentational; all interaction wiring lives in EditorCanvas.
 */
import type { PointerEvent as ReactPointerEvent } from 'react';
import { STATION_RADIUS, flowTypeOption, stationTypeOption } from './constants';
import type { DraftFlow, DraftStation, DraftZone } from './types';

interface StationGlyphProps {
  station: DraftStation;
  selected: boolean;
  flowSource: boolean;
  onPointerDown: (e: ReactPointerEvent<SVGGElement>) => void;
  onClick: (
    e: ReactPointerEvent<SVGGElement> | React.MouseEvent<SVGGElement>,
  ) => void;
}

export function StationGlyph({
  station,
  selected,
  flowSource,
  onPointerDown,
  onClick,
}: StationGlyphProps) {
  const option = stationTypeOption(station.station_type);
  const Icon = option.icon;
  const stroke = flowSource ? '#DD6B20' : selected ? '#3182CE' : '#4A5568';
  return (
    <g
      transform={`translate(${station.canvas_x}, ${station.canvas_y})`}
      onPointerDown={onPointerDown}
      onClick={onClick}
      cursor="pointer"
      data-e2e={`station-glyph-${station.cid}`}
    >
      <circle
        r={STATION_RADIUS}
        fill="white"
        stroke={stroke}
        strokeWidth={selected || flowSource ? 3 : 1.5}
      />
      {!station.is_required && (
        <circle
          r={STATION_RADIUS}
          fill="none"
          stroke={stroke}
          strokeWidth={1}
          strokeDasharray="4 3"
        />
      )}
      <Icon x={-12} y={-12} width={24} height={24} color="#2D3748" />
      <text
        y={STATION_RADIUS + 14}
        textAnchor="middle"
        fontSize={12}
        fill="#2D3748"
        style={{ userSelect: 'none' }}
      >
        {station.name}
      </text>
    </g>
  );
}

interface ZoneGlyphProps {
  zone: DraftZone;
  selected: boolean;
  flowSource: boolean;
  onPointerDown: (e: ReactPointerEvent<SVGGElement>) => void;
  onClick: (e: React.MouseEvent<SVGGElement>) => void;
  onResizePointerDown: (e: ReactPointerEvent<SVGRectElement>) => void;
}

export function ZoneGlyph({
  zone,
  selected,
  flowSource,
  onPointerDown,
  onClick,
  onResizePointerDown,
}: ZoneGlyphProps) {
  const stroke = flowSource ? '#DD6B20' : selected ? '#3182CE' : zone.color;
  return (
    <g
      onPointerDown={onPointerDown}
      onClick={onClick}
      cursor="pointer"
      data-e2e={`zone-glyph-${zone.cid}`}
    >
      <rect
        x={zone.canvas_x}
        y={zone.canvas_y}
        width={zone.canvas_width}
        height={zone.canvas_height}
        fill={zone.color}
        fillOpacity={0.25}
        stroke={stroke}
        strokeWidth={selected || flowSource ? 3 : 1.5}
        rx={4}
      />
      <text
        x={zone.canvas_x + 8}
        y={zone.canvas_y + 18}
        fontSize={12}
        fontWeight={600}
        fill="#2D3748"
        style={{ userSelect: 'none' }}
      >
        {zone.name}
      </text>
      {selected && (
        <rect
          x={zone.canvas_x + zone.canvas_width - 6}
          y={zone.canvas_y + zone.canvas_height - 6}
          width={12}
          height={12}
          fill="#3182CE"
          cursor="nwse-resize"
          onPointerDown={onResizePointerDown}
          data-e2e={`zone-resize-${zone.cid}`}
        />
      )}
    </g>
  );
}

export interface FlowGeometry {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface FlowGlyphProps {
  flow: DraftFlow;
  geometry: FlowGeometry;
  selected: boolean;
  markerId: string;
  onClick: (e: React.MouseEvent<SVGGElement>) => void;
}

export function FlowGlyph({
  flow,
  geometry,
  selected,
  markerId,
  onClick,
}: FlowGlyphProps) {
  const option = flowTypeOption(flow.flow_type);
  const { x1, y1, x2, y2 } = geometry;
  return (
    <g onClick={onClick} cursor="pointer" data-e2e={`flow-glyph-${flow.cid}`}>
      {/* invisible fat stroke = generous hit area */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="transparent"
        strokeWidth={14}
      />
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={selected ? '#3182CE' : option.color}
        strokeWidth={selected ? 3 : 2}
        strokeDasharray={flow.is_primary_path ? undefined : '6 4'}
        markerEnd={`url(#${markerId})`}
      />
    </g>
  );
}
